import AsyncSelect from '@/components/crud/AsyncSelect'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { PageHeader } from '@/components/ui/page-header'
import { SortableTH, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import api from '@/lib/api'
import { formatBRL, formatCnpj, formatDate } from '@/lib/utils'
import {
    Ban,
    CheckCircle2,
    Copy,
    Download,
    FileCode2,
    FilePlus2,
    FileSpreadsheet,
    FileText,
    Plus,
    RefreshCcw,
    RotateCcw,
    Search,
    Send,
    Trash2,
    X
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import BaixarNfsModal from './BaixarNfsModal'
import CaixaModal from './CaixaModal'
import EmailModal from './EmailModal'
import GerarNfModal from './GerarNfModal'
import GerarRascunhoModal from './GerarRascunhoModal'
import ValorDivergente from './ValorDivergente'
import { downloadPdfUrl, openPdfUrl } from './nfFile'
import { NF_STATUS_DEFAULT, statusMeta } from './nfStatus'

const EDITAVEIS = ['rascunho', 'verificado']

export default function NfList() {
  const navigate = useNavigate()
  const toast = useToast()

  const [items, setItems] = useState([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [sort, setSort] = useState({ key: 'id', direction: 'desc' })

  const [q, setQ] = useState('')
  const [clienteID, setClienteID] = useState('')
  const [competencia, setCompetencia] = useState('')
  const [todos, setTodos] = useState(false)

  const [rascunhoOpen, setRascunhoOpen] = useState(false)
  const [gerarOpen, setGerarOpen] = useState(false)
  const [baixarNfsOpen, setBaixarNfsOpen] = useState(false)
  const [caixaOpen, setCaixaOpen] = useState(false)
  const [emailNf, setEmailNf] = useState(null)
  const [excluirNf, setExcluirNf] = useState(null)
  const [eventoNf, setEventoNf] = useState(null)
  const [eventoTipo, setEventoTipo] = useState('cancelar')
  const [eventoMotivo, setEventoMotivo] = useState('')
  const [eventoDescricao, setEventoDescricao] = useState('')
  const [eventoValor, setEventoValor] = useState('')
  const [eventoDescricaoServico, setEventoDescricaoServico] = useState('')
  const [eventoSenha, setEventoSenha] = useState('')
  const [eventoSaving, setEventoSaving] = useState(false)

  const buildParams = useCallback(
    (nextOffset) => ({
      q,
      clienteID,
      competencia,
      status: todos ? '' : NF_STATUS_DEFAULT.join(','),
      sort: sort.key,
      direction: sort.direction,
      limit: 30,
      offset: nextOffset,
    }),
    [q, clienteID, competencia, todos, sort]
  )

  const load = useCallback(
    async (reset) => {
      if (loading) return
      setLoading(true)
      try {
        const nextOffset = reset ? 0 : offset
        const { data } = await api.get('/nfs', { params: buildParams(nextOffset) })
        setItems((prev) => (reset ? data.data : [...prev, ...data.data]))
        setOffset(data.offset)
        setHasMore(data.hasMore)
      } finally {
        setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildParams, offset, loading]
  )

  useEffect(() => {
    const t = setTimeout(() => {
      setOffset(0)
      load(true)
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, clienteID, competencia, todos, sort])

  function handleSort(key) {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  function onScroll(e) {
    const el = e.target
    if (hasMore && !loading && el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
      load(false)
    }
  }

  const hasFilters = q || clienteID || competencia || todos

  function clearFilters() {
    setQ(''); setClienteID(''); setCompetencia(''); setTodos(false)
  }

  // ---------------- Ações da linha ----------------
  async function duplicar(nf) {
    try {
      await api.post(`/nfs/${nf.nfID}/duplicar`)
      toast.success('NF duplicada como rascunho.')
      load(true)
    } catch {
      toast.error('Não foi possível duplicar a NF.')
    }
  }

  async function alternarStatus(nf) {
    const novo = nf.status === 'rascunho' ? 'verificado' : 'rascunho'
    try {
      await api.put(`/nfs/${nf.nfID}/status`, { status: novo })
      setItems((prev) => prev.map((x) => (x.nfID === nf.nfID ? { ...x, status: novo } : x)))
    } catch {
      toast.error('Não foi possível alterar o status.')
    }
  }

  async function baixarArquivo(nf, tipo, action = 'download') {
    try {
      const { data, headers } = await api.get(`/nfs/${nf.nfID}/${tipo}`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([data], { type: headers['content-type'] }))
      if (tipo === 'pdf' && action === 'open') {
        openPdfUrl(url)
      } else if (tipo === 'pdf') {
        downloadPdfUrl(url, nf)
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = `NFSe-${nf.numero || nf.nfID}.xml`
        a.click()
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch {
      toast.error('Arquivo indisponível.')
    }
  }

  async function confirmarExcluir() {
    try {
      await api.delete(`/nfs/${excluirNf.nfID}`)
      toast.success('NF excluída.')
      setExcluirNf(null)
      load(true)
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Não foi possível excluir.')
    }
  }

  function abrirEvento(nf, tipo) {
    setEventoNf(nf)
    setEventoTipo(tipo)
    setEventoMotivo(tipo === 'cancelar' ? '1' : '99')
    setEventoDescricao('')
    setEventoValor(nf.valor ?? '')
    setEventoDescricaoServico(nf.descricaoServico ?? '')
    setEventoSenha('')
  }

  async function confirmarEvento() {
    setEventoSaving(true)
    try {
      const url = `/nfs/${eventoNf.nfID}/${eventoTipo}`
      const payload = { motivo: eventoMotivo, descricao: eventoDescricao, senha: eventoSenha }
      if (eventoTipo === 'substituir') {
        payload.valor = eventoValor
        payload.descricaoServico = eventoDescricaoServico
      }
      const { data } = await api.post(url, payload)
      if (eventoTipo === 'substituir') {
        toast.success(`NF substituída. Nova NF: ${data.substituta}.`)
      } else {
        toast.success('NF cancelada com sucesso.')
      }
      setEventoNf(null)
      load(true)
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Não foi possível concluir o evento fiscal.')
    } finally {
      setEventoSaving(false)
    }
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Notas Fiscais"
        subtitle="Emissão e acompanhamento das NFS-e."
        actions={
          <>
            <Button variant="outline" onClick={() => navigate('/nf/novo')}><Plus className="h-4 w-4" /> Novo</Button>
            <Button variant="outline" onClick={() => setBaixarNfsOpen(true)}>
              <Download className="h-4 w-4" /> Baixar NF’s
            </Button>
            <Button variant="outline" onClick={() => setCaixaOpen(true)}>
              <FileSpreadsheet className="h-4 w-4" /> Caixa
            </Button>
            <Button  onClick={() => setRascunhoOpen(true)}>
              <FilePlus2 className="h-4 w-4" /> Gerar Rascunhos
            </Button>
            <Button onClick={() => setGerarOpen(true)} className="bg-chart-2">
              <Send className="h-4 w-4" /> Gerar NF’s
            </Button>
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              name="busca-nf"
              autoComplete="off"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar ID, NF, CNPJ, cliente, contrato, descrição..."
              className="pl-9"
            />
          </div>

          <div className="w-96">
            <AsyncSelect
              endpoint="/clientes"
              value={clienteID}
              onChange={(v) => setClienteID(v)}
              optionValue="clienteID"
              optionLabel="nome"
              placeholder="Cliente"
            />
          </div>

          <Input type="month" value={competencia} onChange={(e) => setCompetencia(e.target.value)} className="w-auto" title="Mês de competência" />

          <button
            type="button"
            role="switch"
            aria-checked={todos}
            onClick={() => setTodos((v) => !v)}
            className="flex items-center gap-2 ml-1"
            title="Exibir NFs de todos os status"
          >
            <span
              className={
                'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ' +
                (todos ? 'bg-primary' : 'bg-border')
              }
            >
              <span
                className={
                  'inline-block h-5 w-5 transform rounded-full bg-background shadow transition-transform ' +
                  (todos ? 'translate-x-5' : 'translate-x-0.5')
                }
              />
            </span>
            <span className="text-sm">Todos</span>
          </button>

          {hasFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4" /> Limpar
            </Button>
          )}
        </div>
      </PageHeader>

      <div onScroll={onScroll} className="flex-1 overflow-auto p-6 pt-4">
        <Table>
          <THead>
            <tr>
              <SortableTH sortKey="id" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>ID</SortableTH>
              <SortableTH sortKey="emissao" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>Emissão</SortableTH>
              <SortableTH sortKey="numero" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>NF</SortableTH>
              <SortableTH sortKey="cliente" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>Cliente</SortableTH>
              <SortableTH sortKey="cnpj" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>CNPJ</SortableTH>
              <SortableTH sortKey="contrato" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>Contrato</SortableTH>
              <SortableTH sortKey="valor" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort} align="right">Valor</SortableTH>
              <SortableTH sortKey="descricao" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>Descrição</SortableTH>
              <SortableTH sortKey="status" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort} align="center">Status</SortableTH>
              <TH className="text-right">Ações</TH>
            </tr>
          </THead>
          <TBody>
            {items.map((nf, i) => {
              const emitido = nf.status === 'emitido'
              const editavel = EDITAVEIS.includes(nf.status)
              const meta = statusMeta(nf.status)
              return (
                <TR key={nf.nfID} index={i} clickable onClick={() => navigate(`/nf/${nf.nfID}`)}>
                  <TD className="text-muted-foreground tabular-nums">{nf.nfID}</TD>
                  <TD>{nf.dataEmissao ? formatDate(nf.dataEmissao) : <span className="text-muted-foreground">—</span>}</TD>
                  <TD>{nf.numero || <span className="text-muted-foreground">—</span>}</TD>
                  <TD className="font-medium">{nf.clienteNome}</TD>
                  <TD className="tabular-nums">{formatCnpj(nf.clienteCnpj) || <span className="text-muted-foreground">-</span>}</TD>
                  <TD>{nf.numeroContrato}</TD>
                  <TD className="text-right">
                    <span className="inline-flex items-center justify-end gap-1">
                      {formatBRL(nf.valor)}
                      <ValorDivergente status={nf.status} valor={nf.valor} valorContratado={nf.valorContratado} />
                    </span>
                  </TD>
                  <TD className="max-w-55 truncate" title={nf.descricaoServico}>{nf.descricaoServico}</TD>
                  <TD className="text-center"><Badge variant={meta.variant}>{meta.label}</Badge></TD>
                  <TD className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-0.5">
                      <IconBtn title="Duplicar" onClick={() => duplicar(nf)}><Copy className="h-4 w-4" /></IconBtn>
                      {/* <IconBtn title={emitido ? 'Enviar por e-mail' : 'Disponível após emissão'} disabled={!emitido} onClick={() => setEmailNf(nf)}>
                        <span className="relative inline-flex">
                          <MailCheck className="h-4 w-4 relative top-0.5" />
                          {nf.emailsEnviados > 0 && (
                            <span className="absolute -top-1 -right-2 text-[10px] bg-primary text-primary-foreground rounded-full px-1 leading-4 min-w-3.75 text-center">{nf.emailsEnviados}</span>
                          )}
                        </span>
                      </IconBtn> */}
                      <IconBtn title={emitido ? 'Baixar XML' : 'Disponível após emissão'} disabled={!emitido} onClick={() => baixarArquivo(nf, 'xml')}><FileCode2 className="h-4 w-4" /></IconBtn>
                      <IconBtn title={emitido ? 'Abrir PDF (DANFSe)' : 'Disponível após emissão'} disabled={!emitido} onClick={() => baixarArquivo(nf, 'pdf', 'open')}><FileText className="h-4 w-4" /></IconBtn>
                      <IconBtn title={emitido ? 'Baixar PDF' : 'Disponível após emissão'} disabled={!emitido} onClick={() => baixarArquivo(nf, 'pdf', 'download')}><Download className="h-4 w-4" /></IconBtn>
                      <IconBtn title={emitido ? 'Cancelar NF' : 'Disponível após emissão'} disabled={!emitido} onClick={() => abrirEvento(nf, 'cancelar')}><Ban className="h-4 w-4" /></IconBtn>
                      <IconBtn title={emitido ? 'Substituir NF' : 'Disponível após emissão'} disabled={!emitido} onClick={() => abrirEvento(nf, 'substituir')}><RefreshCcw className="h-4 w-4" /></IconBtn>
                      <IconBtn
                        title={editavel ? (nf.status === 'rascunho' ? 'Marcar como verificado' : 'Voltar para rascunho') : 'Indisponível'}
                        disabled={!editavel}
                        onClick={() => alternarStatus(nf)}
                      >
                        {nf.status === 'verificado' ? <RotateCcw className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                      </IconBtn>
                      <IconBtn title={editavel ? 'Excluir' : 'Indisponível'} disabled={!editavel} danger onClick={() => setExcluirNf(nf)}><Trash2 className="h-4 w-4" /></IconBtn>
                    </div>
                  </TD>
                </TR>
              )
            })}
            {items.length === 0 && !loading && (
              <tr>
                <TD colSpan={10} className="text-center text-muted-foreground py-10">Nenhuma NF encontrada.</TD>
              </tr>
            )}
          </TBody>
        </Table>
        {loading && <p className="text-center text-sm text-muted-foreground py-4">Carregando...</p>}
      </div>

      <GerarRascunhoModal open={rascunhoOpen} onClose={() => setRascunhoOpen(false)} onDone={() => { setRascunhoOpen(false); load(true) }} />
      <BaixarNfsModal open={baixarNfsOpen} onClose={() => setBaixarNfsOpen(false)} />
      <CaixaModal open={caixaOpen} onClose={() => setCaixaOpen(false)} />
      {/* onDone só recarrega a listagem por trás: o modal permanece aberto na etapa de resultado. */}
      <GerarNfModal open={gerarOpen} onClose={() => setGerarOpen(false)} onDone={() => load(true)} />
      <EmailModal nf={emailNf} onClose={() => setEmailNf(null)} onSent={() => load(true)} />

      <Dialog
        open={!!excluirNf}
        onClose={() => setExcluirNf(null)}
        title="Excluir NF"
        size="sm"
        footer={<>
          <Button variant="outline" onClick={() => setExcluirNf(null)}>Cancelar</Button>
          <Button variant="destructive" onClick={confirmarExcluir}>Excluir</Button>
        </>}
      >
        <p className="text-sm text-muted-foreground">
          Excluir a NF de <b>{excluirNf?.clienteNome}</b> ({formatBRL(excluirNf?.valor)})? Esta ação não pode ser desfeita.
        </p>
      </Dialog>

      <Dialog
        open={!!eventoNf}
        onClose={() => !eventoSaving && setEventoNf(null)}
        title={eventoTipo === 'cancelar' ? 'Cancelar NF' : 'Substituir NF'}
        description={eventoTipo === 'cancelar'
          ? 'O evento será enviado à SEFIN e a NF só será marcada como cancelada após aceite.'
          : 'Será criada e emitida uma nova NF com os dados atuais. O valor e a descrição podem ser ajustados antes do envio.'}
        footer={<>
          <Button variant="outline" onClick={() => setEventoNf(null)} disabled={eventoSaving}>Voltar</Button>
          <Button variant={eventoTipo === 'cancelar' ? 'destructive' : 'default'} onClick={confirmarEvento} disabled={eventoSaving || !eventoSenha || !eventoMotivo || !eventoValor || !eventoDescricaoServico.trim() || (eventoTipo === 'cancelar' && eventoDescricao.trim().length < 15)}>
            {eventoSaving ? 'Enviando...' : (eventoTipo === 'cancelar' ? 'Cancelar NF' : 'Substituir NF')}
          </Button>
        </>}
      >
        <div className="space-y-4">
          <p className="text-sm">NF <b>{eventoNf?.numero || eventoNf?.nfID}</b> · chave <span className="font-mono text-xs">{eventoNf?.chave}</span></p>
          {eventoTipo === 'substituir' && <>
            <label className="block space-y-1.5"><span className="text-sm font-medium">Valor da NF substituta <span className="text-destructive">*</span></span><Input type="number" min="0" step="0.01" value={eventoValor} onChange={(e) => setEventoValor(e.target.value)} /></label>
            <label className="block space-y-1.5"><span className="text-sm font-medium">Descrição do serviço <span className="text-destructive">*</span></span><textarea value={eventoDescricaoServico} onChange={(e) => setEventoDescricaoServico(e.target.value)} rows={3} className="flex w-full rounded-md border border-border bg-input px-3 py-2 text-sm" /></label>
          </>}
          <label className="block space-y-1.5"><span className="text-sm font-medium">Motivo <span className="text-destructive">*</span></span>
            <select value={eventoMotivo} onChange={(e) => setEventoMotivo(e.target.value)} className="flex h-10 w-full rounded-md border border-border bg-input px-3 text-sm">
              {eventoTipo === 'cancelar' ? <>
                <option value="1">1 — Erro na emissão</option>
                <option value="2">2 — Serviço não prestado</option>
                <option value="9">9 — Outros</option>
              </> : <>
                <option value="01">01 — Desenquadramento do Simples Nacional</option>
                <option value="02">02 — Enquadramento do Simples Nacional</option>
                <option value="03">03 — Inclusão retroativa de imunidade/isenção</option>
                <option value="04">04 — Exclusão retroativa de imunidade/isenção</option>
                <option value="05">05 — Rejeição pelo tomador/intermediário</option>
                <option value="99">99 — Outros</option>
              </>}
            </select>
          </label>
          <label className="block space-y-1.5"><span className="text-sm font-medium">{eventoTipo === 'cancelar' ? 'Descrição do cancelamento' : 'Descrição da substituição'} {eventoTipo === 'cancelar' && <span className="text-destructive">*</span>}</span>
            <textarea value={eventoDescricao} onChange={(e) => setEventoDescricao(e.target.value)} rows={3} maxLength={255} placeholder={eventoTipo === 'cancelar' ? 'Informe o motivo detalhado (mínimo de 15 caracteres).' : 'Descrição opcional do motivo.'} className="flex w-full rounded-md border border-border bg-input px-3 py-2 text-sm" />
          </label>
          <label className="block space-y-1.5"><span className="text-sm font-medium">Senha do certificado <span className="text-destructive">*</span></span>
            <Input type="password" value={eventoSenha} onChange={(e) => setEventoSenha(e.target.value)} autoComplete="new-password" />
          </label>
        </div>
      </Dialog>
    </div>
  )
}

function IconBtn({ title, onClick, disabled, danger, children }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={
        'p-1.5 rounded-md transition-colors ' +
        (disabled
          ? 'text-muted-foreground/40 cursor-not-allowed'
          : danger
          ? 'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
          : 'text-muted-foreground hover:text-foreground hover:bg-accent/60')
      }
    >
      {children}
    </button>
  )
}
